import { FieldPolicy, gql } from '@apollo/client/core';
import type { KeyArgsFunction } from '@apollo/client/cache/inmemory/policies';
import type { StrictTypedTypePolicies } from '../generated/apollo';
import _ from 'lodash';
import { elementsSatisfy } from '@precon-tools-suite-by-shared/js-utils';
import { edgeMerger, edgesMerger, pageInfoMerger } from './mergers';

export const connectionKeyArgs: KeyArgsFunction = (args) => {
    const identifierKeys = _.keys(
        _.omit(args, ['first', 'after', 'last', 'before'])
    );
    return identifierKeys.length > 0 ? identifierKeys : false;
};

export function connectionField(): FieldPolicy {
    return {
        keyArgs: connectionKeyArgs,
        merge: true,
    };
}

const typePolicies: StrictTypedTypePolicies = {
    ObjectIdentity: {},
    SignInActivity: {},
    Mutation: {
        mutationType: true,
    },
    Query: {
        subscriptionType: true,
        queryType: true,
        fields: {
            companies: connectionField(),
            contacts: connectionField(),
            countries: connectionField(),
            questionPredicates: connectionField(),
            regions: connectionField(),
            productPredicates: connectionField(),
            risks: connectionField(),
            riskConfigurations: connectionField(),
            supplierReliabilities: connectionField(),
            teams: connectionField(),
            threats: connectionField(),
            users: connectionField(),
            accountsWithRole: connectionField(),
            userInvitations: connectionField(),
            audits: connectionField(),
            riskAudits: connectionField(),
            riskProbabilityAudits: connectionField(),
            productCategoryAudits: connectionField(),
            threatAudits: connectionField(),
            threatSeverityAudits: connectionField(),
            threatCategoryAudits: connectionField(),
            verificationActions: connectionField(),
            verificationConfigurations: connectionField(),
            teamLicenses: connectionField(),
            teamLicenseClasses: connectionField(),
            verificationPredicates: connectionField(),
            node: {
                read(value, { args, toReference }) {
                    if (value) return value;
                    if (!args?.id || typeof args.id !== 'string')
                        return undefined;
                    return toReference(args.id);
                },
            },
            nodes: {
                read(value, { args, toReference, cache }) {
                    if (value) return value;
                    const ids: string[] = args?.ids ?? [];
                    const exists = (id: string) =>
                        !!cache.readFragment({
                            id,
                            fragment: gql`
                                fragment NodeInfo on Node {
                                    id
                                    __typename
                                }
                            `,
                        });

                    if (elementsSatisfy(ids, exists)) {
                        return ids.map((id) => toReference(id));
                    }
                    return undefined;
                },
            },
        },
    },
    Node: {
        merge: true,
    },
    AccessToken: {
        merge: true,
    },
    MutationPayload: {},
    Ownable: {},
    Usable: {},
    Owner: {},
    Todo: {},
    AssignProductCategoryToProductTodo: {},
    AssignSupplierBranchToProductTodo: {},
    ResolveAnalysisReportProductRiskPredicateValueTodo: {},
    SelectAnalysisReportProductsTodo: {},
    SetRiskProbabilityValueForRiskConfigurationTodo: {},
    SetThreatSeverityValueForRiskConfigurationTodo: {},
    PredicateRequirement: {},
    ProductPredicateRequirement: {},
    ProductPredicateRequirementDemandingContextConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    ProductPredicateRequirementDemandingContextEdge: { merge: edgeMerger },
    AnalysisEntryPredicate: {},
    AnalysisEntryPredicateConnection: {},
    AnalysisEntryPredicateEdge: {},
    AnalysisEntryPredicateExpression: {},
    AnalysisReport_RiskConfigurationConnection: {},
    AnalysisReport_RiskConfigurationEdge: {},
    Audit: {
        fields: {
            comments: connectionField(),
            tags: connectionField(),
        },
    },
    AnalysisReportProductRisk_ProductPredicateConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    AnalysisReportProductRisk_ProductPredicateEdge: { merge: edgeMerger },
    AuditConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    AuditEdge: {
        merge: edgeMerger,
    },
    Client: {},
    GeneralAudit: {},
    ProductCategoryAudit: {},
    ProductCategoryAuditConnection: {},
    ProductCategoryAuditEdge: {},
    RiskAudit: {},
    RiskAuditConnection: {},
    RiskAuditEdge: {},
    RiskProbabilityAudit: {},
    RiskProbabilityAuditConnection: {},
    RiskProbabilityAuditEdge: {},
    ThreatAudit: {},
    ThreatAuditConnection: {},
    ThreatAuditEdge: {},
    ThreatCategoryAudit: {},
    ThreatCategoryAuditConnection: {},
    ThreatCategoryAuditEdge: {},
    ThreatSeverityAudit: {},
    ThreatSeverityAuditConnection: {},
    ThreatSeverityAuditEdge: {},
    UpdateAudit_SuccessResult: {},
    UpdateAudits_SuccessResult: {},
    Company_ContactConnection: {},
    Company_ContactEdge: {},
    ConnectionWithTotal: {},
    ExportAnalysisReportEntriesJob: {
        fields: {
            files: connectionField(),
            analysisReportEntries: connectionField(),
        },
    },
    ExportSupplierVCardsJob: {
        fields: {
            files: connectionField(),
        },
    },
    ExportSupplierBranchesSpreadsheetJob: {
        fields: {
            files: connectionField(),
            supplierBranches: connectionField(),
        },
    },
    ExportProductsSpreadsheetJob: {
        fields: {
            files: connectionField(),
            products: connectionField(),
        },
    },
    ExportSuppliersSpreadsheetJob: {
        fields: {
            files: connectionField(),
            suppliers: connectionField(),
        },
    },
    EchoFilesJob: {
        fields: {
            files: connectionField(),
        },
    },
    EchoFiles_FileDescription: {},
    Hashed: {},
    ProductCategory_RiskConnection: {},
    ProductCategory_RiskEdge: {},
    Product_QuestionPredicateConnection: {},
    Product_QuestionPredicateEdge: {},
    Product_RegionConnection: {},
    Product_RegionEdge: {},
    Product_RiskConnection: {},
    Product_RiskEdge: {},
    Query_CompanyConnection: {},
    Query_CountryConnection: {},
    Query_RegionConnection: {},
    Query_RiskConfigurationConnection: {},
    Query_SupplierReliabilityConnection: {},
    Query_VerificationActionConnection: {},
    Query_VerificationConfigurationConnection: {},
    Query_TeamConnection: {},
    RiskConfiguration_RiskConnection: {},
    RiskConfiguration_RiskEdge: {},
    RiskConfiguration_ThreatConnection: {},
    RiskConfiguration_ThreatEdge: {},
    Risk_ProductCategoryConnection: {},
    Risk_ProductCategoryEdge: {},
    Risk_RiskConfigurationConnection: {},
    Risk_RiskConfigurationEdge: {},
    SupplierReliability: {},
    SupplierReliabilityEdge: {},
    SupplierReliabilityConnection: {},
    Team_ProductConnection: {},
    Team_ProductEdge: {},
    Team_RiskConfigurationConnection: {},
    Team_RiskConfigurationEdge: {},
    Team_SupplierConnection: {},
    Team_SupplierEdge: {},
    Team_UserConnection: {},
    Team_UserEdge: {},
    Team_VerificationConfigurationConnection: {},
    Team_VerificationConfigurationEdge: {},
    Threat_RiskConfigurationConnection: {},
    Threat_RiskConfigurationEdge: {},
    UpdateAnalysisReportEntry_SuccessResult: {},
    UpdateRiskConfiguration_SuccessResult: {},
    User_TeamConnection: {},
    User_TeamEdge: {},

    Activateable: {},
    Selectable: {},
    Timestamped: {},
    Userstamped: {},
    Sluggable: {},
    SoftDelete: {},
    SoftDeleteUserstamped: {},
    // SimplePaginatorInfo: {},
    Taggable: {
        fields: {
            tags: connectionField(),
        },
    },
    HasFiles: {
        fields: {
            files: connectionField(),
        },
    },
    Tag: {},
    TagEdge: {},
    TagConnection: {},
    Commentable: {
        fields: {
            comments: connectionField(),
        },
    },
    Comment: {},
    CommentEdge: {},
    CommentConnection: {},
    AnalysisReport: {
        fields: {
            entries: connectionField(),
            products: connectionField(),
        },
    },
    AnalysisReportEdge: {},
    AnalysisReportConnection: {},
    AnalysisReportEntry: {
        fields: {
            verificationActions: connectionField(),
            probabilityOverwriteEvents: connectionField(),
            severityOverwriteEvents: connectionField(),
        },
    },
    AnalysisReportEntryRiskValue: {},
    AnalysisReportEntryProbability: {},
    AnalysisReportEntrySeverity: {},
    AnalysisReportEntryRiskLevel: {},
    AnalysisReportEntryProbabilityOverwriteEvent: {},
    AnalysisReportEntrySeverityOverwriteEvent: {},
    AnalysisReportEntryRiskValueOverwriteEvent: {},
    AnalysisReportEntry_ProbabilityOverwriteEventConnection: {},
    AnalysisReportEntry_SeverityOverwriteEventConnection: {},
    AnalysisReportEntry_ProbabilityOverwriteEventEdge: {},
    AnalysisReportEntry_SeverityOverwriteEventEdge: {},
    AnalysisEntry: {},
    AnalysisReportEntryEdge: {},
    AnalysisReportEntryConnection: {},
    AnalysisReportEntry_VerificationActionConnection: {},
    AnalysisReportEntry_VerificationActionEdge: {},
    AnalysisReportEntry_VerificationPredicateConnection: {},
    AnalysisReportEntry_VerificationPredicateEdge: {},
    AnalysisReportProductRisk: {
        fields: {
            comments: connectionField(),
            productPredicates: connectionField(),
        },
    },
    AnalysisReportProductRiskConnection: {},
    AnalysisReportProductRiskEdge: {},
    AnalysisReportRiskConfigurationChangedWarning: {},
    AnalysisReportTodoConnection: {},
    AnalysisReportTodoEdge: {
        merge: true,
    },
    Message: {},
    AnalysisReportTodoListNotEmptyException: {},
    AnalysisReportVerificationConfigurationChangedWarning: {},
    DateTimePeriod: {},
    GenerateAnalysisReport_SuccessResult: {},
    Exception: {},
    ProductPredicateRequirementConnection: {
        merge: true,
        fields: { edges: { merge: false } },
    },
    ProductPredicateRequirementEdge: {},
    ProvideRequiredProductPredicateValuesTodo: {},
    Warning: {},
    ClearAnalysisReportEntries_SuccessResult: {},
    ClearAnalysisReportEntryVerificationActionsResult: {},
    ClearAnalysisReportEntryVerificationPredicateValues_SuccessResult: {},
    ClearAnalysisReportProducts_SuccessResult: {},
    ClearAnalysisReportProductRisks_SuccessResult: {},
    ClearAnalysisReportRiskConfigurations_SuccessResult: {},
    CreateAnalysisReport_SuccessResult: {},
    GenerateAnalysisReportEntriesResult: {},
    GenerateAnalysisReportEntryVerificationActionsResult: {},
    GenerateAnalysisReportProductRisksResult: {},
    GenerateNeededAnalysisReportEntryVerificationPredicateValuesResult: {},
    GenerateNeededAnalysisReportProductPredicateValuesResult: {},
    UpdateAnalysisReport_SuccessResult: {},
    VerificationConfiguration: {
        fields: {
            actions: connectionField(),
            tags: connectionField(),
            comments: connectionField(),
            usedQuestionPredicates: connectionField(),
            usedRegions: connectionField(),
            usedVerificationPredicates: connectionField(),
            usedRiskConfigurationLevels: connectionField(),
            usedRiskConfigurationProbabilities: connectionField(),
            usedRiskConfigurationSeverities: connectionField(),
            usedRiskConfigurations: connectionField(),
            rules: connectionField(),
        },
    },
    VerificationAction: {
        fields: {
            tags: connectionField(),
            comments: connectionField(),
        },
    },
    VerificationActionConnection: {},
    VerificationActionEdge: {},
    VerificationPredicate: {
        fields: {
            tags: connectionField(),
            comments: connectionField(),
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
        },
    },
    VerificationPredicateConnection: {},
    VerificationPredicateEdge: {},
    VerificationConfiguration_VerificationActionConnection: {},
    VerificationConfiguration_VerificationActionEdge: {},
    VerificationConfigurationConnection: {},
    VerificationConfigurationEdge: {},
    VerificationConfigurationRule: {
        fields: {
            verificationActions: connectionField(),
            usedQuestionPredicates: connectionField(),
            usedRegions: connectionField(),
            usedVerificationPredicates: connectionField(),
            usedRiskConfigurationLevels: connectionField(),
            usedRiskConfigurationProbabilities: connectionField(),
            usedRiskConfigurationSeverity: connectionField(),
            usedRiskConfigurations: connectionField(),
        },
    },
    VerificationConfigurationRule_VerificationActionConnection: {},
    VerificationConfigurationRule_VerificationActionEdge: {},
    VerificationConfigurationRuleConnection: {},
    VerificationConfigurationRuleEdge: {},
    VerifyAnalysisReportEntriesHaveNeededVerificationPredicateValuesResult: {},
    VerifyAnalysisReportProductRiskValuesResult: {},
    VerifyAnalysisReportProductsBelongToProductCategoriesResult: {},
    VerifyAnalysisReportProductsHaveNeededPredicateValuesResult: {},
    VerifyAnalysisReportProductsHaveSupplierBranchesResult: {},
    Predicate: { merge: true },
    PredicateExpression: { keyFields: ['hash'] },
    PredicateValueSet: {},
    CreateQuestionPredicate_SuccessResult: {},
    QuestionPredicate: {
        fields: {
            usedByRisks: connectionField(),
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
            products: connectionField(),
            tags: connectionField(),
            comments: connectionField(),
        },
    },
    QuestionPredicateConnection: {},
    QuestionPredicateEdge: {},
    UpdateQuestionPredicate_SuccessResult: {},
    Product_PredicateConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    Product_PredicateEdge: {
        merge: edgeMerger,
    },
    ProductPredicateExpression: {},
    PredicateProductConnection: {},
    PredicateProductEdge: {},
    PredicateRiskConnection: {},
    PredicateRiskEdge: {},
    ProductPredicate: {},
    ProductPredicateConnection: {},
    ProductPredicateEdge: {},
    Company: {
        fields: {
            contacts: connectionField(),
            teams: connectionField(),
        },
    },
    CompanyEdge: {},
    CompanyConnection: {},
    Contact: {
        fields: {
            accounts: connectionField(),
            userInvitations: connectionField(),
        },
    },
    ContactEdge: {},
    ContactConnection: {},
    NestedSetNode: {
        fields: {
            ancestors: connectionField(),
            parent: connectionField(),
            descendants: connectionField(),
            bloodline: connectionField(),
            children: connectionField(),
            siblings: connectionField(),
        },
    },
    NestedSetEdge: {},
    NestedSetConnection: {},
    ProductCategory: {
        fields: {
            products: connectionField(),
            risks: connectionField(),
        },
    },
    ProductCategoryEdge: {},
    ProductCategoryConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    Product: {
        fields: {
            risks: connectionField(),
            questionPredicates: connectionField(),
            predicateRequirements: connectionField(),
            regions: connectionField(),
            productCategories: connectionField(),
            suppliers: connectionField(),
            supplierBranches: connectionField(),
            predicates: connectionField(),
            comments: connectionField(),
            tags: connectionField(),
        },
    },
    ProductEdge: {},
    ProductConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    AccountConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    AccountEdge: {},
    Region: {
        fields: {
            products: connectionField(),
            usedByRisks: connectionField(),
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
        },
    },
    RegionEdge: {},
    RegionConnection: {},
    Country: {},
    CountryEdge: {},
    RiskConfiguration: {
        fields: {
            probabilities: connectionField(),
            severities: connectionField(),
            levels: connectionField(),
        },
    },
    RiskConfigurationConnection: {},
    RiskConfigurationEdge: {},
    RiskConfigurationSegment: {},
    RiskConfigurationLevel: {
        fields: {
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
        },
    },
    RiskConfigurationLevelEdge: {},
    RiskConfigurationLevelConnection: {},
    RiskConfigurationSeverity: {
        fields: {
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
        },
    },
    RiskConfigurationSeverityEdge: {},
    RiskConfigurationSeverityConnection: {},
    RiskConfigurationProbability: {
        fields: {
            usedByVerificationConfigurationRules: connectionField(),
            usedByVerificationConfigurations: connectionField(),
        },
    },
    RiskConfigurationProbabilityEdge: {},
    RiskConfigurationProbabilityConnection: {},
    Risk: {
        fields: {
            regions: connectionField(),
            riskConfigurations: connectionField(),
            riskColumns: connectionField(),
        },
    },
    RiskConfigurationSegmentConnection: {},
    RiskConfigurationSegmentEdge: {},
    RiskColumn: {
        fields: {
            risks: connectionField(),
        },
    },
    Risk_RiskColumnConnection: {},
    Risk_RiskColumnEdge: {},
    RiskColumn_RiskConnection: {},
    RiskColumn_RiskEdge: {},
    RiskColumnConnection: {},
    RiskColumnEdge: {},
    UpdateRiskColumn_SuccessResult: {},
    CreateRiskColumn_SuccessResult: {},
    RiskEdge: {},
    RiskConnection: {},
    RiskLevel: { merge: true },
    RiskProbability: { merge: true },
    RiskSeverity: { merge: true },
    RiskValue: { merge: true },
    Supplier: {
        fields: {
            supplierBranches: connectionField(),
        },
    },
    SupplierEdge: {},
    SupplierConnection: {},
    SupplierBranch: {
        fields: {
            products: connectionField(),
        },
    },
    SupplierBranchEdge: {},
    SupplierBranchConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    ThreatCategory: {
        fields: {
            threats: connectionField(),
        },
    },
    ThreatCategoryEdge: {},
    ThreatCategoryConnection: {},
    Threat: {
        fields: {
            risks: connectionField(),
            riskConfigurations: connectionField(),
            comments: connectionField(),
        },
    },
    ThreatEdge: {},
    ThreatConnection: {},
    Team: {
        fields: {
            users: connectionField(),
            analysisReports: connectionField(),
            ownedAnalysisReports: connectionField(),
            suppliers: connectionField(),
            ownedSuppliers: connectionField(),
            usableSuppliers: connectionField(),
            supplierBranches: connectionField(),
            ownedSupplierBranches: connectionField(),
            usableSupplierBranches: connectionField(),
            products: connectionField(),
            ownedProducts: connectionField(),
            usableProducts: connectionField(),
            ownedProductCategories: connectionField(),
            usableProductCategories: connectionField(),
            threatCategories: connectionField(),
            ownedThreatCategories: connectionField(),
            usableThreatCategories: connectionField(),
            ownedRiskConfigurations: connectionField(),
            usableRiskConfigurations: connectionField(),
            ownedVerificationConfigurations: connectionField(),
            usableVerificationConfigurations: connectionField(),
            ownedVerificationPredicates: connectionField(),
            usableVerificationPredicates: connectionField(),
            ownedVerificationActions: connectionField(),
            usableVerificationActions: connectionField(),
            usableAnalysisEntryPredicates: connectionField(),
            usableProductPredicates: connectionField(),
            usableQuestionPredicates: connectionField(),
            usableRegions: connectionField(),
            usableSupplierReliabilities: connectionField(),
            jobs: connectionField(),
            ownedRisks: connectionField(),
            usableRisks: connectionField(),
            ownedThreats: connectionField(),
            usableThreats: connectionField(),
            licenses: connectionField(),
        },
    },
    TeamEdge: {},
    TeamConnection: {},
    TeamLicense: {},
    TeamLicenseConnection: {},
    TeamLicenseEdge: {},
    TeamLicenseClass: {
        fields: {
            licenses: connectionField(),
        },
    },
    TeamLicenseClassConnection: {},
    TeamLicenseClassEdge: {},
    User: {
        fields: {
            teams: connectionField(),
            jobs: connectionField(),
            contacts: connectionField(),
        },
    },
    UserEdge: {},
    UserConnection: {},
    UserInvitation: {
        fields: {
            teams: connectionField(),
        },
    },
    UserInvitationConnection: {},
    UserInvitationEdge: {},
    CancelUserInvitation_SuccessResult: {},
    CreateUserInvitation_SuccessResult: {},
    SendUserInvitation_SuccessResult: {},
    Team_UserInvitationConnection: {},
    Team_UserInvitationEdge: {},
    UpdateUserInvitation_SuccessResult: {},
    UserInvitation_TeamConnection: {},
    UserInvitation_TeamEdge: {},
    Notifiable: {
        fields: {
            notifications: connectionField(),
        },
    },
    Notification: {},
    NotificationConnection: {},
    NotificationEdge: {},
    File: {},
    FileConnection: {},
    FileEdge: {},
    Job: {
        fields: {
            files: connectionField(),
        },
    },
    JobConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    JobEdge: {
        merge: edgeMerger,
    },
    Query_ProductPredicateConnection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    Query_ProductPredicateEdge: {
        merge: edgeMerger,
    },
    StartJob_SuccessResult: {},
    SendNotification_SuccessResult: {},
    UpdateAnalysisReportProductRisk_SuccessResult: {},
    UpdateAnalysisReportProductRisks_SuccessResult: {},
    FinishJob_SuccessResult: {},
    FailJob_SuccessResult: {},
    Named: {},
    EntityName: { merge: true },
    Name: {},
    PersonName: {},
    PersonNameHonorific: { merge: true },
    PersonNameLast: { merge: true },
    ContactName: {},
    ContactNameLast: {},
    ContactNameHonorific: {},
    UserName: {},
    Address: { merge: true },
    AddressFormat: { merge: true },
    PhoneNumber: { merge: true },
    PhoneNumberFormat: { merge: true },
    Connection: {
        merge: true,
        fields: { edges: { merge: edgesMerger } },
    },
    Edge: { merge: edgeMerger },
    PageInfo: { merge: pageInfoMerger },
    ActivateMany_SuccessResult: {},
    Activate_SuccessResult: {},
    AffineTransformation: {},
    AzureBlobStorageFileUploadRequest: {},
    BlobClientRequest: {},
    FileUploadRequest: {},
    UploadFile_SuccessResult: {},
    GenericFileUploadRequest: {},
    CreateComment_SuccessResult: {},
    CreateCompany_SuccessResult: {},
    CreateContact_SuccessResult: {},
    CreateProductCategory_SuccessResult: {},
    CreateProduct_SuccessResult: {},
    CreateRegion_SuccessResult: {},
    CreateRiskConfiguration_SuccessResult: {},
    CreateRisk_SuccessResult: {},
    CreateSupplierBranch_SuccessResult: {},
    CreateSupplier_SuccessResult: {},
    CreateTeam_SuccessResult: {},
    CreateThreatCategory_SuccessResult: {},
    CreateThreat_SuccessResult: {},
    CreateUser_SuccessResult: {},
    CreateVerificationConfiguration_SuccessResult: {},
    CreateVerificationAction_SuccessResult: {},
    CreateVerificationPredicate_SuccessResult: {},
    UpdateVerificationPredicate_SuccessResult: {},
    UpdateVerificationAction_SuccessResult: {},
    UpdateVerificationConfiguration_SuccessResult: {},
    UpdateVerificationConfigurationRule_SuccessResult: {},
    DeactivateMany_SuccessResult: {},
    Deactivate_SuccessResult: {},
    Delete_SuccessResult: {},
    Restore_SuccessResult: {},
    ForceDelete_SuccessResult: {},
    UpdateComment_SuccessResult: {},
    UpdateComments_SuccessResult: {},
    UpdateCompanies_SuccessResult: {},
    UpdateCompany_SuccessResult: {},
    UpdateContact_SuccessResult: {},
    UpdateContacts_SuccessResult: {},
    UpdateProductCategory_SuccessResult: {},
    UpdateProduct_SuccessResult: {},
    UpdateProducts_SuccessResult: {},
    UpdateRegion_SuccessResult: {},
    UpdateRisk_SuccessResult: {},
    UpdateRisks_SuccessResult: {},
    UpdateSupplierBranch_SuccessResult: {},
    UpdateSupplierBranches_SuccessResult: {},
    UpdateSupplier_SuccessResult: {},
    UpdateSuppliers_SuccessResult: {},
    UpdateTeam_SuccessResult: {},
    UpdateTeams_SuccessResult: {},
    UpdateThreatCategory_SuccessResult: {},
    UpdateThreat_SuccessResult: {},
    UpdateThreats_SuccessResult: {},
    UpdateUser_SuccessResult: {},
    UpdateUsers_SuccessResult: {},
    MeiliSearchTokenResponse: {},
    RebuildSearchIndexes_SuccessResult: {},
    CreateMeiliSearch_SuccessResult: {},
    AssignTeamRole_SuccessResult: {},
    AssignTeamRoles_SuccessResult: {},
    AssignUserRole_SuccessResult: {},
    AssignUserRoles_SuccessResult: {},
    RetractTeamRole_SuccessResult: {},
    RetractTeamRoles_SuccessResult: {},
    RetractUserRole_SuccessResult: {},
    RetractUserRoles_SuccessResult: {},
    CreateTeamLicenseClass_SuccessResult: {},
    CreateTeamLicense_SuccessResult: {},
    UpdateTeamLicenseClass_SuccessResult: {},
    UpdateTeamLicense_SuccessResult: {},
};

export default typePolicies;
